import { useState } from "react";
import ReachUs from "@/components/ReachUs";
import Altcha from "@/components/Altcha";

const HOURS = [
  { day: "Monday – Friday", time: "6:30 am – 6:00 pm" },
  { day: "Saturday", time: "7:00 am – 5:00 pm" },
  { day: "Sunday", time: "7:00 am – 5:00 pm" },
  { day: "Holidays", time: "By appointment" },
];

type FormState = {
  // "down" means the send did not happen and we do not know when it will —
  // the visitor is shown a way to reach a human, never a thank-you.
  status: "idle" | "loading" | "success" | "error" | "down";
  message: string;
};

// The wholesale enquiry form in src/pages/wholesale/components/EnquiryForm.tsx
// posts to the same route and repeats the send-and-report logic below. Anything
// fixed here about how a failure is reported has to be fixed there too.

export default function Visit() {
  const [form, setForm] = useState<FormState>({ status: "idle", message: "" });
  const [token, setToken] = useState("");
  // Set when the verification widget cannot appear at all. While it is set the
  // form cannot be sent, so we do not offer a Send button that would only fail.
  const [verificationDown, setVerificationDown] = useState(false);
  // An answer is good for one send; bump this to start a fresh one.
  const [challengeKey, setChallengeKey] = useState(0);

  const handleUnavailable = (reason: string) => {
    console.error(`contact form: verification unavailable — ${reason}`);
    setVerificationDown(true);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setForm({ status: "success", message: "Thank you — we'll write back soon." });
      formEl.reset();
      return;
    }

    const message = String(formData.get("message") ?? "");
    if (message.length > 500) {
      setForm({ status: "error", message: "Message must be 500 characters or fewer." });
      return;
    }

    if (!token) {
      setForm({
        status: "error",
        message: "Please complete the verification below, then send again.",
      });
      return;
    }

    setForm({ status: "loading", message: "" });

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: String(formData.get("name") ?? ""),
          email: String(formData.get("email") ?? ""),
          topic: String(formData.get("topic") ?? ""),
          message,
          altcha: token,
        }),
      });
      const body = await response.json().catch(() => ({}) as { ok?: boolean; error?: string });

      if (response.ok && body.ok === true) {
        setForm({
          status: "success",
          message: "Thank you — your message is with us. We'll write back within one working day.",
        });
        formEl.reset();
      } else if (response.status >= 400 && response.status < 500 && body.error) {
        // Something the visitor can put right — say what it is, in place.
        setForm({ status: "error", message: body.error });
      } else {
        setForm({ status: "down", message: "" });
      }
    } catch {
      // Offline, blocked, the Function never answered — we do not know it sent,
      // so we must not say it did.
      setForm({ status: "down", message: "" });
    } finally {
      setToken("");
      setChallengeKey((key) => key + 1);
    }
  };

  return (
    <section id="visit" className="w-full bg-background-100 py-24 md:py-32 px-6 md:px-10 lg:px-14">
      <div className="max-w-7xl mx-auto">
        <div className="max-w-2xl mb-16 reveal">
          <span className="text-xs tracking-[0.35em] uppercase text-accent-700">Come Say Hello</span>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl text-primary-950 mt-4 leading-tight">
            The door is <em>always warm.</em>
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          {/* Hours & Location */}
          <div className="lg:col-span-4 reveal">
            <div className="bg-background-50 border border-background-200 rounded-lg p-8">
              <h3 className="font-heading text-2xl text-primary-950 mb-6">Hours</h3>
              <ul className="space-y-4 mb-10">
                {HOURS.map((h) => (
                  <li key={h.day} className="flex items-baseline justify-between gap-4 border-b border-background-200 pb-3">
                    <span className="text-sm text-foreground-800">{h.day}</span>
                    <span className="text-sm text-primary-900 font-medium">{h.time}</span>
                  </li>
                ))}
              </ul>

              <h3 className="font-heading text-2xl text-primary-950 mb-4">Find Us</h3>
              <div className="flex items-start gap-3 mb-3">
                <i className="ri-map-pin-2-line text-accent-700 mt-1 w-5 h-5 flex items-center justify-center"></i>
                <p className="text-sm text-foreground-800 leading-relaxed">
                  1420 SE Division St<br />
                  Portland, Oregon 97202
                </p>
              </div>
              <div className="flex items-center gap-3 mb-3">
                <i className="ri-phone-line text-accent-700 w-5 h-5 flex items-center justify-center"></i>
                <a href="tel:+15550102030" className="text-sm text-foreground-800 hover:text-accent-700 cursor-pointer">
                  (555) 010-2030
                </a>
              </div>
              <div className="flex items-center gap-3">
                <i className="ri-mail-line text-accent-700 w-5 h-5 flex items-center justify-center"></i>
                <a href="mailto:hello@meridiancoffee.example" className="text-sm text-foreground-800 hover:text-accent-700 cursor-pointer">
                  hello@meridiancoffee.example
                </a>
              </div>
            </div>
          </div>

          {/* Map */}
          <div className="lg:col-span-8 reveal">
            <div className="w-full h-64 md:h-80 lg:h-[26rem] rounded-lg overflow-hidden border border-background-200 mb-10">
              <iframe
                title="Meridian Coffee Roasters Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2795.7091027934884!2d-122.63730092361738!3d45.52285483866013!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x54950a04a4a8e4c9%3A0x0!2sE%20Burnside%20St%2C%20Portland%2C%20OR!5e0!3m2!1sen!2sus!4v1700000000000"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>

            {/* Contact Form */}
            <form
              id="contact-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-background-50 border border-background-200 rounded-lg p-8"
            >
              <h3 className="font-heading text-2xl text-primary-950 mb-2">Write to us</h3>
              <p className="text-sm text-foreground-600 mb-6">
                Wholesale, private events, or just a good coffee question — we read every note.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="name" className="block text-xs uppercase tracking-widest text-foreground-600 mb-2">
                    Your Name
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-4 py-3 text-sm bg-background-50 border border-background-300 rounded-md focus:outline-none focus:border-accent-500 transition-colors"
                    placeholder="Anna Reyes"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-xs uppercase tracking-widest text-foreground-600 mb-2">
                    Email
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-4 py-3 text-sm bg-background-50 border border-background-300 rounded-md focus:outline-none focus:border-accent-500 transition-colors"
                    placeholder="anna@example.com"
                  />
                </div>
              </div>

              <div className="mt-5">
                <label htmlFor="topic" className="block text-xs uppercase tracking-widest text-foreground-600 mb-2">
                  Topic
                </label>
                <select
                  id="topic"
                  name="topic"
                  className="w-full px-4 py-3 text-sm bg-background-50 border border-background-300 rounded-md focus:outline-none focus:border-accent-500 transition-colors cursor-pointer"
                  defaultValue="General"
                >
                  <option>General</option>
                  <option>Wholesale</option>
                  <option>Private Events</option>
                  <option>Press</option>
                  <option>Careers</option>
                </select>
              </div>

              <div className="mt-5">
                <label htmlFor="message" className="block text-xs uppercase tracking-widest text-foreground-600 mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-3 text-sm bg-background-50 border border-background-300 rounded-md focus:outline-none focus:border-accent-500 transition-colors resize-none"
                  placeholder="Tell us what&apos;s on your mind…"
                />
                <span className="text-xs text-foreground-500 mt-1 block">Up to 500 characters.</span>
              </div>

              {/* Honeypot */}
              <div className="wa-field-alt" aria-hidden="true">
                <label htmlFor="website_alt">Website</label>
                <input
                  id="website_alt"
                  name="website_alt"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                />
              </div>

              {/* The check the Function performs, put where the visitor can satisfy it. */}
              <Altcha
                resetKey={challengeKey}
                onToken={setToken}
                onUnavailable={handleUnavailable}
              />

              {verificationDown && (
                <div className="mt-6">
                  <ReachUs reason="This form cannot be sent at the moment." />
                </div>
              )}

              {form.status === "down" && !verificationDown && (
                <div className="mt-6">
                  <ReachUs reason="Sorry — that message did not go through." />
                </div>
              )}

              <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <button
                  type="submit"
                  disabled={form.status === "loading" || verificationDown}
                  className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-primary-900 text-background-50 text-sm font-medium hover:bg-primary-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <span>{form.status === "loading" ? "Sending…" : "Send Message"}</span>
                  <i className="ri-send-plane-2-line w-4 h-4 flex items-center justify-center"></i>
                </button>

                {form.status === "success" && (
                  <p className="text-sm text-accent-700 flex items-center gap-2">
                    <i className="ri-check-line w-4 h-4 flex items-center justify-center"></i>
                    {form.message}
                  </p>
                )}
                {form.status === "error" && (
                  <p className="text-sm text-secondary-700 flex items-center gap-2">
                    <i className="ri-error-warning-line w-4 h-4 flex items-center justify-center"></i>
                    {form.message}
                  </p>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}